/**
 * 任务清单卡片组件
 *
 * 显示规划者提交的任务清单，支持：
 * - 结构化任务列表展示
 * - 任务编号和描述
 * - 审批状态指示器
 * - 异议显示
 */

import React from 'react';
import type { TaskList, TaskItem, TaskStatus } from '../../types/agentTeam';

interface TaskListCardProps {
  taskList: TaskList;
  plannerName?: string;
  supervisorName?: string;
  createdAt?: string;
}

const STATUS_STYLES: Record<TaskStatus, { bg: string; text: string; dot: string }> = {
  pending: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    dot: 'bg-gray-400',
  },
  running: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    dot: 'bg-blue-400',
  },
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    dot: 'bg-green-400',
  },
  failed: {
    bg: 'bg-red-100',
    text: 'text-red-600',
    dot: 'bg-red-400',
  },
  cancelled: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-600',
    dot: 'bg-yellow-400',
  },
};

const TASK_TYPE_LABELS: Record<string, string> = {
  explore: '探索',
  write: '创作',
  research: '研究',
  analysis: '分析',
  general: '通用',
};

const APPROVAL_STYLES = {
  pending: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: '⏳',
  },
  approved: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: '✅',
  },
  rejected: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: '❌',
  },
};

const TaskItemRow: React.FC<{
  task: TaskItem;
  index: number;
}> = React.memo(({ task, index }) => {
  const statusStyle = STATUS_STYLES[task.status] || STATUS_STYLES.pending;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${statusStyle.bg} border border-gray-100`}>
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-medium text-gray-900 truncate">{task.name}</h4>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle.text}`}>
            {TASK_TYPE_LABELS[task.task_type] || task.task_type}
          </span>
        </div>
        <p className="text-xs text-gray-600 line-clamp-2">{task.description}</p>
        {task.assignee_name && (
          <div className="mt-1 text-xs text-gray-500">
            执行者: {task.assignee_name}
          </div>
        )}
        {task.dependencies && task.dependencies.length > 0 && (
          <div className="mt-1 text-xs text-gray-400">
            依赖: 任务 {task.dependencies.map(d => `#${d}`).join(', ')}
          </div>
        )}
      </div>
      <div className="flex-shrink-0">
        <span className={`inline-block w-2 h-2 rounded-full ${statusStyle.dot}`} />
      </div>
    </div>
  );
});

TaskItemRow.displayName = 'TaskItemRow';

export const TaskListCard: React.FC<TaskListCardProps> = ({
  taskList,
  plannerName = '规划者',
  supervisorName = '监督者',
  createdAt,
}) => {
  const approvalStyle = APPROVAL_STYLES[taskList.approval_status || 'pending'];

  return (
    <div className="bg-white border-2 border-amber-200 rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 border-b border-amber-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">📋</span>
            <h3 className="text-lg font-semibold text-amber-900">
              {taskList.title || '任务清单'}
            </h3>
          </div>
          {createdAt && (
            <span className="text-xs text-gray-500">
              {new Date(createdAt).toLocaleString('zh-CN')}
            </span>
          )}
        </div>
        {taskList.description && (
          <p className="mt-1 text-sm text-amber-700">{taskList.description}</p>
        )}
        <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
          <span>规划者: {plannerName}</span>
          <span>监督者: {supervisorName}</span>
          <span>任务数: {taskList.tasks.length}</span>
          {taskList.estimated_time && <span>预估: {taskList.estimated_time}</span>}
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-3">
          {taskList.tasks.map((task, index) => (
            <TaskItemRow key={task.id} task={task} index={index} />
          ))}
        </div>
      </div>

      {taskList.approval_status && (
        <div className={`px-4 py-3 ${approvalStyle.bg} ${approvalStyle.border} border-t`}>
          <div className="flex items-center gap-2">
            <span className="text-lg">{approvalStyle.icon}</span>
            <span className={`font-medium ${approvalStyle.text}`}>
              {taskList.approval_status === 'approved' && '监督者已批准'}
              {taskList.approval_status === 'rejected' && '监督者提出异议'}
              {taskList.approval_status === 'pending' && '等待监督者审批'}
            </span>
          </div>
          {taskList.objections && taskList.objections.length > 0 && (
            <div className="mt-2 space-y-1">
              {taskList.objections.map((objection, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded p-2"
                >
                  <span>⚠️</span>
                  <span>{objection}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskListCard;